import React, { useEffect, useState } from "react";
import Button from "../../components/ui/Button";
import StatusBadge from "../../components/ui/StatusBadge";
import { useOutletContext, useParams } from "react-router-dom";
import Spinner from "../../components/ui/Spinner";
import { toast } from "react-toastify";
import { Step } from "../../helpers/stepHelper";
import { DocumentType, getDocumentByType } from "../../helpers/documentHelper";
import WordIcon from "../../assets/icons/word";
import { convertAmountToPretty, strToFormattedDate } from "../../helpers/formatHelper";
import Table from "../../components/ui/Table";
import { getColumnByType } from "../../helpers/tableHelper";
import Loader from "../../components/ui/Loader";
import Card from "../../components/ui/Card";
import { updateProcurement, getOrderByProcurementUuid } from "../../services/api/procurementService";
import { OfferStatus } from "../../helpers/bidderHelper";

export default function Order() {
    const { procurement, handleUpdateProcurement } = useOutletContext();
    const { uuidProcurement } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [order, setOrder] = useState(null);
    const [offerSelected, setOfferSelected] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formUrl, setFormUrl] = useState("");
    const enterpriseSelected = procurement.enterprises.find(enterprise => enterprise.offers[0]?.status === OfferStatus.SELECTED);
    const document = getDocumentByType(procurement.documents, [DocumentType.ORDER_SIGNED, DocumentType.ORDER]);

    const columns = [
        {
            key: "description",
            title: "Description du service fourni",
            type: getColumnByType.TEXT,
        },
        {
            key: "amountVAT",
            title: "Montant TVA comprise",
            type: getColumnByType.AMOUNT,
        },
    ];

    useEffect(() => {
        async function fetch() {
            try {
                setIsLoading(true);
                const result = await getOrderByProcurementUuid(uuidProcurement);
                if (result) {
                    setOrder(result.order);
                    setOfferSelected(result.offerSelected);
                }
            } catch (error) {
                toast.error(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetch();
    }, [uuidProcurement]);

    useEffect(() => {
        const handleMessage = async (event) => {
            if (event.data?.action === "submission-completed") {
                try {
                    setIsGenerating(true);
                    setShowForm(false);
                    await updateProcurement(uuidProcurement, { isGenerating: true });
                    await handleUpdateProcurement();
                    if (procurement.step.name === Step.WRITING_ORDER) {
                        toast.success(procurement.orderEditLink ? "Modifications envoyées." : "Données envoyées.");
                    } else if (procurement.step.name === Step.VALIDATION_ORDER) {
                        toast.success("Envoi du bon de commande signé avec succès.");
                    }
                } catch (error) {
                    toast.error(error.message);
                } finally {
                    setIsGenerating(false);
                }
            }
        };

        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    // eslint-disable-next-line
    }, [uuidProcurement]);

    function getOrderInfoInHtml(services) {
        let html = `Date de réception de l'offre : <b>${strToFormattedDate(offerSelected.submittedAt)}</b><br />`;

        services.forEach((service, index) => {
            if (service.libelle !== "") {
                html += `Service ${index + 1} :<ul><li>Description : ${service.description}</li><li>Montant TVA : <b>${convertAmountToPretty(service.amountVAT)}</b></li></ul>`;
            }
        });

        return html;
    }

    function handleButton(url) {
        setFormUrl(url);
        setShowForm(true);
    }

    function OrderButton() {
        if (procurement.step.name === Step.WRITING_ORDER && procurement.isUserAction) {
            if (!procurement.orderEditLink) {
                return (
                    <div className="flex flex-col items-end mb-6">
                        <Button
                            onClick={
                                () => handleButton(`${process.env.REACT_APP_JOTFORM_PROCUREMENT_WRITING_ORDER}?ids=${procurement.idSubmission}&saisissezUne=${enterpriseSelected.enterpriseNumber ?? ""}&saisissezUne92=${enterpriseSelected.name ?? "NOM"}&saisissezUne93=${enterpriseSelected.rawStreet ?? ""}&chiffre96=${enterpriseSelected.postalCode ?? ""}&saisissezUne94=${enterpriseSelected.locality ?? ""}`)
                            }
                            disabled={procurement.isGenerating}
                        >
                            <span>Rédiger le bon de commande</span>
                        </Button>
                        {procurement.isGenerating && <span className="text-gray-500 italic text-sm mt-2">Génération des documents en cours...</span>}
                    </div>
                );
            } else {
                return (
                    <div className="flex flex-col items-end mb-6">
                        <Button
                            onClick={
                                () => handleButton(`${procurement.orderEditLink}`)
                            }
                            disabled={procurement.isGenerating}
                        >
                            <span>Éditer le bon de commande</span>
                        </Button>
                        {procurement.isGenerating && <span className="text-gray-500 italic text-sm mt-2">Modification en cours de traitement...</span>}
                    </div>
                );
            }
        }

        if (procurement.step.name === Step.VALIDATION_ORDER && procurement.isUserAction) {
            return (
                <div className="flex flex-col items-end mb-6">
                    <Button
                        onClick={
                            () => handleButton(`${process.env.REACT_APP_JOTFORM_PROCUREMENT_ORDER_SIGNED}?ids=${procurement.idSubmission}&info=${getOrderInfoInHtml(order.services)}`)
                        }
                        disabled={procurement.isGenerating}
                    >
                        <span>Envoyer le bon de commande signé</span>
                    </Button>
                    {procurement.isGenerating && <span className="text-gray-500 italic text-sm mt-2">Envoi des emails en cours...</span>}
                </div>
            );
        }

        return null;
    }

    if (isLoading) {
        return (
            <Spinner />
        );
    }

    if (isGenerating) {
        return <Loader text="Chargement des données en cours... Merci de votre patience 😊" />;
    }

    return (
        <>
            {
                !showForm
                    ? (
                        <>
                            <OrderButton />
                            {
                                !order
                                    ? (
                                        <div className="h-full self-center content-center">
                                            <StatusBadge text="Aucun bon de commande rédigé" color="red" />
                                        </div>
                                    )
                                    : (
                                        <div className="flex flex-col space-y-10">
                                            <div className="flex justify-between">
                                                <div className="space-y-6">
                                                    <h2 className="text-2xl font-semibold">Bon de commande</h2>
                                                    {document
                                                        ? (
                                                            <div
                                                            className="w-10 h-auto cursor-pointer text-blue-600 flex items-center gap-2"
                                                            onClick={() => window.open(document.url, "_blank")}
                                                            >
                                                            <WordIcon />
                                                            </div>
                                                        )
                                                        : isGenerating
                                                            ? (
                                                            <p className="text-gray-500">
                                                                Document en cours de génération{" "}
                                                                <span className="text-sm italic">
                                                                (veuillez rafraîchir la page d’ici quelques secondes).
                                                                </span>
                                                            </p>
                                                            )
                                                            : (
                                                            <span className="text-gray-500">
                                                                Aucun document{" "}
                                                                <span className="text-sm italic">
                                                                (il sera généré après validation du formulaire)
                                                                </span>
                                                            </span>
                                                            )}

                                                </div>
                                                <div className="space-y-6">
                                                    <h2 className="text-2xl font-semibold">Date de réception de l'offre</h2>
                                                    <p>
                                                        {strToFormattedDate(offerSelected.submittedAt)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="space-y-6">
                                                <h2 className="text-2xl font-semibold">Service(s)</h2>
                                                <Table columns={columns} data={order.services} showSum />
                                            </div>
                                            <div className="flex space-x-6">
                                                {order.deposit
                                                    && (
                                                        <Card title="Caution">
                                                            <p>
                                                                {convertAmountToPretty(order.deposit)}
                                                            </p>
                                                        </Card>
                                                    )}
                                                {order.additionalCosts
                                                    && (
                                                        <Card title="Charge(s) additionnelle(s)">
                                                            <p>
                                                                {convertAmountToPretty(order.additionalCosts)}
                                                            </p>
                                                        </Card>
                                                    )}
                                                {order.provision
                                                    && (
                                                        <Card title="Provision">
                                                            <p>
                                                                {convertAmountToPretty(order.provision)}
                                                            </p>
                                                        </Card>
                                                    )}
                                            </div>
                                        </div>
                                    )
                            }
                        </>
                    )
                    : (
                        <div className="h-full relative">
                            <div className="absolute right-4">
                                <Button
                                    onClick={
                                        () => setShowForm(false)
                                    }
                                >
                                    <span>Annuler</span>
                                </Button>
                            </div>
                            <iframe
                                id="JotFormIFrame-250133237867963"
                                title="Marché public - Bon de commande"
                                onLoad={() => window.parent.scrollTo(0, 0)}
                                src={formUrl}
                                className="border rounded-lg shadow-md h-full w-full"
                            >
                            </iframe>
                        </div>
                    )
            }
        </>
    );
}
